import React, { useState, useEffect, useContext, useRef } from 'react';

import { useBridgeApi } from 'Hooks';
import { Location } from '../../utils/location';
import { Language } from '../../Types';
import { DEFAULT_LOCALE } from '../../actions';
const LOCALSTORAGE_KEY = 'platform-storage';

export type Device = { token: string; operatingSystem: string; registered: boolean } | null;

export type AnonymousOrder = { id: string; createdAt: string };

export interface StorageData {
  cookieConsent: boolean | null;
  analyticsConsent: boolean | null;
  authToken: string | null;
  temporaryAuthToken: string | null;
  selectedPaymentMethod: string | null;
  enableNotifications: boolean;
  device: Device;
  userLocation: Location | null;
  cartId: string | null;
  serviceType: string | null;
  anonymousOrders: AnonymousOrder[];
  language: Language;
  qrOrder: boolean;
  redirectPath: string;
}

/**
 * !!! WHEN ADDING A NEW STORAGE FIELD !!!
 * Also add default data to this constant so that we dont
 * have to do null-checks for missing data everywhere
 */
const DEFAULT_DATA: StorageData = {
  cookieConsent: null,
  analyticsConsent: null,
  authToken: null,
  temporaryAuthToken: null,
  selectedPaymentMethod: null,
  enableNotifications: true,
  device: null,
  userLocation: null,
  cartId: null,
  serviceType: null,
  anonymousOrders: [],
  language: DEFAULT_LOCALE,
  qrOrder: false,
  redirectPath: '/',
};

type UpdateFunction = (data: Partial<StorageData>) => void;
type Loading = boolean;

const Context = React.createContext([
  DEFAULT_DATA,
  (data) => {
    // Placeholder
  },
  true,
] as [StorageData, UpdateFunction, Loading]);

export const StorageProvider: React.FunctionComponent = ({ children }) => {
  const [data, setData] = useState<StorageData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const api = useBridgeApi();

  const loaded = useRef(false);

  useEffect(() => {
    if (window.hasNativeWrapper) {
      // If we are using a native wrapper, use native storage
      //api.getStorageData().then((newData) => setData((current) => ({ ...current, ...newData })));
      api.getStorageData().then((newData) => {
        setData({ ...DEFAULT_DATA, ...newData });
        setLoading(false);
      });
    } else {
      // Otherwise, use computer local storage
      const newData = localStorage.getItem(LOCALSTORAGE_KEY) || '{}';
      const parsedData = JSON.parse(newData);

      setData({ ...DEFAULT_DATA, ...parsedData });
      setLoading(false);
    }

    loaded.current = true;
  }, []);

  /**
   * Updates permanent storage with our settings data
   *
   * @param storageData One or more keys from the storage data interface
   */
  function update(storageData: Partial<StorageData>) {
    if (loading) {
      console.warn(
        '!!! WARNING !!! Trying to update storage before data loading is finished. Possibly overwriting with DEFAULT_DATA.',
      );
    }
    if (!loaded.current) {
      return;
    }

    const newData = {
      ...data,
      ...storageData,
    };

    setData((current) => ({ ...current, ...storageData }));

    if (window.hasNativeWrapper) {
      return api.updateStorage(newData);
    } else {
      /* Store data in local storage for development */
      return new Promise((resolve) => {
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(newData));
        resolve(true);
      });
    }
  }

  return <Context.Provider value={[data, update, loading]}>{children}</Context.Provider>;
};

export const useStorage = () => {
  return useContext(Context);
};
